"use client";
import { useState } from "react";
import { roleLabel } from "../console-shell";
import { Banner, Button, Field, FieldGrid, Input } from ".";
import {
  Choice,
  CommandForm,
  PersonPicker,
  ReadState,
  useRead,
  type PersonOption
} from "./record-tools";
import {
  flatten,
  type OrgNode,
  type OrgKind,
  type Term,
  type Appointment,
  type MoveImpact
} from "./organization-model";
import type { DictionaryItem } from "./member-model";
export type OrgAction =
  "create" | "rename" | "move" | "archive" | "term" | "appointment" | "end" | "close" | "supersede";
const TITLES: Record<OrgAction, string> = {
  create: "新建下级组织",
  rename: "更名与区划",
  move: "调整上级组织",
  archive: "归档组织",
  term: "登记新届次",
  appointment: "登记会内任职",
  end: "办理卸任",
  close: "收届",
  supersede: "更正任职"
};
export function OrganizationEditor({
  action,
  org,
  term,
  appointment,
  tree,
  kinds,
  divisions,
  posts,
  onCancel,
  onSaved
}: {
  action: OrgAction;
  org: OrgNode;
  term?: Term;
  appointment?: Appointment;
  tree: OrgNode[];
  kinds: OrgKind[];
  divisions: DictionaryItem[];
  posts: DictionaryItem[];
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const [initial] = useState({
    name: action === "rename" ? org.name : "",
    divisionCode: action === "rename" ? (org.divisionCode ?? "") : "",
    shortName: "",
    kindCode: "",
    parentId: "",
    ordinal: "",
    displayName: "",
    startOn: "",
    sourceRef: "",
    reason: "",
    endOn: "",
    postCode: ""
  });
  const [form, setForm] = useState(initial),
    [makeCurrent, setMakeCurrent] = useState(false),
    [confirmed, setConfirmed] = useState(false),
    [person, setPerson] = useState<PersonOption | null>(null);
  const [previewParent, setPreviewParent] = useState("");
  const preview = useRead<MoveImpact>(
    previewParent
      ? `/api/v1/orgs/${encodeURIComponent(org.id)}/move-preview?parentId=${encodeURIComponent(previewParent)}`
      : null
  );
  const change = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const input = (
    key: keyof typeof form,
    label: string,
    required = false,
    type = "text",
    minLength = 0,
    maxLength = 200
  ) => (
    <Field label={label}>
      <Input
        type={type}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        min={type === "number" ? 1 : undefined}
        value={form[key]}
        onChange={(e) => change(key, e.target.value)}
      />
    </Field>
  );
  const danger = ["archive", "close", "end", "supersede"].includes(action);
  const path = `/api/v1/orgs/${encodeURIComponent(org.id)}`;
  const descendants = new Set(flatten([org]).map(({ node }) => node.id));
  return (
    <CommandForm
      title={TITLES[action]}
      description={`${org.name}${term ? ` · ${term.displayName}` : ""}${appointment ? ` · ${appointment.displayName} / ${appointment.postLabel}` : ""}`}
      dirty={
        JSON.stringify(form) !== JSON.stringify(initial) || makeCurrent || confirmed || !!person
      }
      onCancel={onCancel}
      onSaved={onSaved}
      danger={danger}
      label={action === "move" ? "确认调整" : danger ? `确认${TITLES[action]}` : "保存登记"}
      disabled={
        (danger && !confirmed) ||
        (action === "move" && (!preview.data || previewParent !== form.parentId || !confirmed)) ||
        (action === "appointment" && !person)
      }
      onSubmit={async (send) => {
        if (action === "create")
          return send("/api/v1/orgs", {
            name: form.name.trim(),
            parentId: org.id,
            ...(form.kindCode ? { kindCode: form.kindCode } : {}),
            ...(form.shortName.trim() ? { shortName: form.shortName.trim() } : {}),
            ...(form.divisionCode ? { divisionCode: form.divisionCode } : {})
          });
        if (action === "rename")
          return send(`${path}/rename`, {
            name: form.name.trim(),
            expectedVersion: org.version,
            ...(form.divisionCode ? { divisionCode: form.divisionCode } : {})
          });
        if (action === "move")
          return send(`${path}/move`, {
            parentId: form.parentId,
            expectedVersion: org.version,
            acknowledgedImpactDigest: preview.data!.impactDigest
          });
        if (action === "archive")
          return send(`${path}/archive`, {
            expectedVersion: org.version,
            reason: form.reason.trim()
          });
        if (action === "term")
          return send(`${path}/terms`, {
            ordinal: Number(form.ordinal),
            displayName: form.displayName.trim(),
            sourceRef: form.sourceRef.trim(),
            makeCurrent,
            ...(form.startOn ? { startOn: form.startOn } : {}),
            placeholderRef: "XXX-TERM-FACTS"
          });
        if (action === "appointment")
          return send(`/api/v1/terms/${encodeURIComponent(term!.id)}/appointments`, {
            personId: person!.personId,
            postCode: form.postCode,
            sourceRef: form.sourceRef.trim(),
            ...(form.startOn ? { startOn: form.startOn } : {})
          });
        if (action === "supersede") return send(`/api/v1/appointments/${encodeURIComponent(appointment!.id)}/supersede`, { expectedVersion: appointment!.version, reason: form.reason.trim() });
        if (action === "end")
          return send(`/api/v1/appointments/${encodeURIComponent(appointment!.id)}/end`, {
            expectedVersion: appointment!.version,
            endOn: form.endOn,
            endedReason: form.reason.trim()
          });
        return send(`/api/v1/terms/${encodeURIComponent(term!.id)}/close`, {
          expectedVersion: term!.version,
          reason: form.reason.trim()
        });
      }}
    >
      <section className="record-section">
        <h2>办理内容</h2>
        {(action === "create" || action === "rename") && (
          <FieldGrid>
            {input("name", "组织全称", true, "text", 2)}
            {action === "create" && (
              <>
                {input("shortName", "组织简称")}
                <Choice
                  label="组织类型"
                  value={form.kindCode}
                  empty="暂不标注"
                  onChange={(v) => change("kindCode", v)}
                  options={kinds.map((k) => ({ value: k.code, label: k.displayName }))}
                />
              </>
            )}
            <Choice
              label="所在区划"
              value={form.divisionCode}
              empty={action === "rename" ? "保持原区划" : "暂不登记"}
              onChange={(v) => change("divisionCode", v)}
              options={divisions.map((d) => ({ value: d.itemCode, label: d.displayName }))}
            />
          </FieldGrid>
        )}
        {action === "move" && (
          <>
            <Choice
              label="新的上级组织"
              required
              value={form.parentId}
              onChange={(v) => {
                change("parentId", v);
                setPreviewParent("");
                setConfirmed(false);
              }}
              options={flatten(tree)
                .filter(
                  ({ node }) =>
                    !descendants.has(node.id) &&
                    node.id !== org.parentId &&
                    node.lifecycle === "ACTIVE" &&
                    node.kind?.canHaveChildren !== false
                )
                .map(({ node }) => ({ value: node.id, label: node.name }))}
            />
            <Button
              type="button"
              variant="outline"
              disabled={!form.parentId || preview.loading}
              onClick={() => {
                if (previewParent === form.parentId) void preview.reload();
                else setPreviewParent(form.parentId);
                setConfirmed(false);
              }}
            >
              预览授权影响
            </Button>
            <ReadState {...preview} retry={preview.reload} />
            {preview.data && (
              <section className="record-section">
                <h3>调整后，以下授权的覆盖范围将改变</h3>
                {preview.data.grants.length ? (
                  <ul className="record-impact">
                    {preview.data.grants.map((g, i) => (
                      <li key={i}>
                        <strong>{g.accountLabel}</strong>
                        <span>{roleLabel(g.roleCode)}</span>
                        <span>
                          {g.effect === "GAINS" ? "增加" : "减少"} {g.organizationCount} 个组织
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>未发现动态授权覆盖范围的变化。</p>
                )}
                <p className="record-muted">
                  如授权范围在确认前变化，系统会拒绝本次调整，需重新预览。
                </p>
              </section>
            )}
          </>
        )}
        {action === "term" && (
          <>
            <FieldGrid>
              {input("ordinal", "届数", true, "number")}
              {input("displayName", "届次名称", true, "text", 2)}
              {input("startOn", "开始日期", false, "date")}
            </FieldGrid>
            <label className="record-check">
              <input
                type="checkbox"
                checked={makeCurrent}
                onChange={(e) => setMakeCurrent(e.target.checked)}
              />
              登记为现届
            </label>
            <p className="record-muted">已有现届时，请先完成收届。未确认的届次日期保持待核实。</p>
          </>
        )}
        {action === "appointment" && (
          <>
            <PersonPicker
              endpoint={`/api/v1/terms/${encodeURIComponent(term!.id)}/candidates`}
              value={person}
              onChange={setPerson}
            />
            <p className="record-muted">仅列出你负责组织范围内的有效会籍。选择会员后登记任职。</p>
            <FieldGrid>
              <Choice
                label="会内职务"
                required
                value={form.postCode}
                onChange={(v) => change("postCode", v)}
                options={posts.map((p) => ({ value: p.itemCode, label: p.displayName }))}
              />
              {input("startOn", "就任日期", false, "date")}
            </FieldGrid>
          </>
        )}
        {action === "archive" && (
          <Banner tone="warning">
            归档后该组织停止日常使用，记录保留。系统会检查有效会籍、下级组织、授权和未办结事项；有阻断项时不会归档。
          </Banner>
        )}
        {action === "close" && (
          <Banner tone="warning">
            本届 {term?.liveAppointmentCount ?? 0}{" "}
            条在任记录将不再计为现任，相关职务授权及其转授会失效。不会替任职记录填写未知的卸任日期。
          </Banner>
        )}
        {action === "supersede" && <Banner tone="warning">更正后这条任职收档，不再作为现任或授权依据，相关任职授权失效。原记录保留在本届任职表；正确任职需另行登记。</Banner>}
        {action === "end" && (
          <>
            <Banner tone="warning">
              卸任后，依据这条任职签发的授权及其转授会失效，历史任职继续保留。
            </Banner>
            {input("endOn", "卸任日期", true, "date")}
          </>
        )}
      </section>
      {["term", "appointment"].includes(action) && (
        <section className="record-section">
          <h2>登记依据</h2>
          {input("sourceRef", "来源依据", true, "text", 2)}
        </section>
      )}
      {danger && (
        <section className="record-section">
          {input("reason", "办理理由", true, "text", 4, 500)}
        </section>
      )}
      {(danger || (action === "move" && preview.data)) && (
        <label className="record-check">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          我已核对组织、人员与上述影响
        </label>
      )}
    </CommandForm>
  );
}
